import { servicesUrlConfig } from "shared/config/setting";

/**
 * Robots API Service
 * Centralized API calls for robot management
 */
export const robotsService = {
  /**
   * Get all robots with pagination
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 0 to fetch all)
   * @returns {Promise} API response with robots data
   */
  async getAll(authFetch, page = 1, perPage = 0) {
    const url = `${servicesUrlConfig.ROBOTS_API.get_all}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get robots for current user (associated robots via organizations)
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 0 to fetch all)
   * @returns {Promise} API response with user's associated robots
   */
  async getMe(authFetch, page = 1, perPage = 0) {
    const url = `${servicesUrlConfig.ROBOTS_API.me}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get robots created by current user
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 0 to fetch all)
   * @returns {Promise} API response with user's robots
   */
  async getMyRobots(authFetch, page = 1, perPage = 0) {
    const url = `${servicesUrlConfig.ROBOTS_API.my_robots}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get current user's robot roles
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 0 to fetch all)
   * @returns {Promise} API response with user's robot roles
   */
  async getMyRobotRoles(authFetch, page = 1, perPage = 0) {
    const url = `${servicesUrlConfig.ROBOTS_API.base_url}/my-robot-roles?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get robot by ID
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @returns {Promise} API response with robot details
   */
  async getById(authFetch, robotId) {
    const url = `${servicesUrlConfig.ROBOTS_API.get_by_id}${robotId}`;
    return await authFetch(url);
  },

  /**
   * Create a new robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {Object} robotData - Robot data {robot_id, name, type_id, organizations, password}
   * @returns {Promise} API response
   */
  async create(authFetch, robotData) {
    const url = servicesUrlConfig.ROBOTS_API.create;
    return await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(robotData),
    });
  },

  /**
   * Update an existing robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @param {Object} updateData - Data to update {name, type_id, organizations, password}
   * @returns {Promise} API response
   */
  async update(authFetch, robotId, updateData) {
    const url = `${servicesUrlConfig.ROBOTS_API.update}${robotId}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Delete a robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @returns {Promise} API response
   */
  async delete(authFetch, robotId) {
    const url = `${servicesUrlConfig.ROBOTS_API.delete}${robotId}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },

  /**
   * Generate new API key for a robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @returns {Promise} API response with updated robot
   */
  async generateApiKey(authFetch, robotId) {
    const url = `${servicesUrlConfig.ROBOTS_API.base_url}/api-key/${robotId}`;
    return await authFetch(url, {
      method: "POST",
    });
  },

  /**
   * Reset password for a robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @param {string} newPassword - New password for the robot
   * @returns {Promise} API response with updated robot
   */
  async resetPassword(authFetch, robotId, newPassword) {
    const url = `${servicesUrlConfig.ROBOTS_API.base_url}/reset-password/${robotId}?new_password=${encodeURIComponent(newPassword)}`;
    return await authFetch(url, {
      method: "PUT",
    });
  },

  /**
   * Upload icon for a robot
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @param {File} iconFile - Icon file to upload
   * @returns {Promise} API response
   */
  async uploadIcon(authFetch, robotId, iconFile) {
    const url = `${servicesUrlConfig.ROBOTS_API.base_url}/${robotId}/icon`;
    const formData = new FormData();
    formData.append("file", iconFile);

    return await authFetch(url, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });
  },

  /**
   * Send command to a robot (superuser only)
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} robotId - Robot ID
   * @param {Object} commandData - Command data to send
   * @returns {Promise} API response with robot
   */
  async sendCommand(authFetch, robotId, commandData) {
    const url = `${servicesUrlConfig.ROBOTS_API.base_url}/send_robot_command/${robotId}`;
    return await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(commandData),
    });
  },

  /**
   * Get preset icons for robot selection
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with preset icons
   */
  async getPresetIcons(authFetch) {
    const url = servicesUrlConfig.ROBOTS_API.get_preset_icons;
    return await authFetch(url);
  },

  // Robot Types Methods

  /**
   * Get all robot types
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 100)
   * @returns {Promise} API response with robot types
   */
  async getRobotTypes(authFetch, page = 1, perPage = 100) {
    const url = `${servicesUrlConfig.ROBOT_TYPES_API.get_all}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Create a new robot type
   * @param {function} authFetch - Authenticated fetch function
   * @param {Object} robotTypeData - Robot type data {name, description, icon_path}
   * @returns {Promise} API response
   */
  async createRobotType(authFetch, robotTypeData) {
    const url = servicesUrlConfig.ROBOT_TYPES_API.create;
    return await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(robotTypeData),
    });
  },

  /**
   * Update an existing robot type
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} typeId - Robot type ID
   * @param {Object} updateData - Data to update {name, description}
   * @returns {Promise} API response
   */
  async updateRobotType(authFetch, typeId, updateData) {
    const url = `${servicesUrlConfig.ROBOT_TYPES_API.update}${typeId}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Delete a robot type
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} typeId - Robot type ID
   * @returns {Promise} API response
   */
  async deleteRobotType(authFetch, typeId) {
    const url = `${servicesUrlConfig.ROBOT_TYPES_API.delete}${typeId}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },

  /**
   * Upload icon for a robot type
   * @param {function} authFetch - Authenticated fetch function
   * @param {string} typeId - Robot type ID
   * @param {File} iconFile - Icon file to upload
   * @returns {Promise} API response
   */
  async uploadRobotTypeIcon(authFetch, typeId, iconFile) {
    const url = `${servicesUrlConfig.ROBOT_TYPES_API.upload_icon}${typeId}/icon`;
    const formData = new FormData();
    formData.append("file", iconFile);

    return await authFetch(url, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });
  },
};
